import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  getOrphanStandAddonKeys,
  getStandAddonKeys,
  getUpdatedParentKey,
  scheduleOrphanStandCleanup,
} from '@theme/arca-stand-cart';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('change', this.#onStandChange);

    scheduleOrphanStandCleanup({
      hasStandRows: () => this.isConnected && Boolean(this.querySelector('.arca-stand-addon')),
      cleanup: () => this.#cleanupOrphanStandAddons(),
      schedule: (callback) => requestAnimationFrame(callback),
    });
  }

  /**
   * Removes hidden paid stand lines after their panel has already disappeared.
   */
  async #cleanupOrphanStandAddons() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await response.json();
      if (!response.ok) throw new Error('Unable to read cart while cleaning orphan stands.');

      const orphanStandKeys = getOrphanStandAddonKeys(cart);
      if (!orphanStandKeys.length) return;

      this.updateQuantity({
        line: 1,
        quantity: 0,
        action: 'cleanup',
        itemKeys: orphanStandKeys,
      });
    } catch (error) {
      console.error('Unable to clean orphan stand lines:', error);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('change', this.#onStandChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      // Remove the paid ARCA stand that belongs to this panel as well.
      ...this.refs.cartItemRows.filter((row) => row.dataset.arcaStandParentKey === cartItemRowToRemove.dataset.key),
    ];

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      itemKeys: rowsToRemove.map((row) => row.dataset.key).filter(Boolean),
    });

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string[]} [config.itemKeys] - Cart line keys to update together.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const hasMultipleItems = config.itemKeys && config.itemKeys.length > 0;
    const body = JSON.stringify(
      hasMultipleItems
        ? {
            updates: Object.fromEntries(config.itemKeys.map((itemKey) => [itemKey, quantity])),
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }
        : {
            line: line,
            quantity: quantity,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }
    );

    cartTotal?.shimmer();

    fetch(`${hasMultipleItems ? Theme.routes.cart_update_url : Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles ARCA stand property changes in the cart.
   * @param {Event} event
   */
  #onStandChange = async (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-arca-cart-stand-select]')) return;

    const line = Number.parseInt(select.dataset.line || '', 10);
    if (!line) return;

    const option = select.selectedOptions[0];
    const properties = this.#getLineProperties(select);
    const currentStandVariantId = properties._arca_stand_variant_id || '';
    const standTitle = option?.dataset.standTitle || option?.textContent?.trim() || select.value;
    const standPriceLabel = option?.dataset.standPriceLabel || '';

    properties.Stand = standPriceLabel ? `${standTitle} (${standPriceLabel})` : standTitle;
    properties._arca_stand_handle = select.value;
    properties._arca_stand_variant_id = option?.dataset.standVariantId || '';
    properties._arca_stand_price = option?.dataset.standPrice || '0';

    const parentRow = select.closest('.cart-items__table-row');
    const parentKey = parentRow?.dataset.key || '';
    const quantity = Number(parentRow?.querySelector('input[type="number"]')?.value) || 1;

    await this.#updateLineProperties(line, properties, {
      parentKey,
      currentStandVariantId,
      nextStandVariantId: option?.dataset.standVariantId || '',
      nextStandPrice: Number(option?.dataset.standPrice) || 0,
      quantity,
    });
  };

  /**
   * @param {HTMLSelectElement} select
   * @returns {Record<string, string>}
   */
  #getLineProperties(select) {
    const script = select.closest('.cart-items__stand-picker')?.querySelector('[data-arca-line-properties]');
    if (!script?.textContent) return {};

    try {
      const parsed = JSON.parse(script.textContent);
      if (Array.isArray(parsed)) {
        return Object.fromEntries(parsed.filter((entry) => Array.isArray(entry) && entry.length >= 2));
      }

      if (parsed && typeof parsed === 'object') {
        return { ...parsed };
      }
    } catch (error) {
      console.error('Failed to parse ARCA line properties:', error);
    }

    return {};
  }

  /**
   * Updates line item properties and refreshes cart sections.
   * @param {number} line
   * @param {Record<string, string>} properties
   * @param {{parentKey: string, currentStandVariantId: string, nextStandVariantId: string, nextStandPrice: number, quantity: number}} stand
   */
  async #updateLineProperties(line, properties, stand) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('stand-property:user-action');

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = Array.from(sectionsToUpdate).join(',');
    const body = JSON.stringify({
      line,
      properties,
      sections,
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await cartResponse.json();
      if (!cartResponse.ok) throw new Error('Unable to read cart before updating stand.');

      const standKeysToRemove = getStandAddonKeys(cart, stand.parentKey, stand.currentStandVariantId);

      let response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      let data = await response.json();

      if (!response.ok || data.errors) {
        this.#handleCartError(line, data);
        return;
      }

      const updatedParentKey = getUpdatedParentKey(data, line, stand.parentKey);

      // Replace every previous paid stand belonging to this panel before adding
      // the new one. This makes the cart total deterministic instead of additive.
      if (standKeysToRemove.length) {
        response = await fetch(
          Theme.routes.cart_update_url,
          fetchConfig('json', {
            body: JSON.stringify({
              updates: Object.fromEntries(standKeysToRemove.map((key) => [key, 0])),
              sections,
              sections_url: window.location.pathname,
            }),
          })
        );
        data = await response.json();
        if (!response.ok || data.errors) throw new Error(data.description || data.message || 'Unable to remove previous stand.');
      }

      if (stand.nextStandVariantId && stand.nextStandPrice > 0) {
        response = await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({
              items: [
                {
                  id: Number(stand.nextStandVariantId),
                  quantity: stand.quantity,
                  properties: {
                    _arca_stand_addon: 'true',
                    _arca_stand_parent_key: updatedParentKey,
                  },
                },
              ],
              sections,
              sections_url: window.location.pathname,
            }),
          })
        );
        data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add selected stand.');
      }

      const newSectionHTML = new DOMParser().parseFromString(data.sections[this.sectionId], 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent(data, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: data.sections,
        })
      );

      morphSection(this.sectionId, data.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });
      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error('Unable to update selected stand:', error);
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
